import { useRef } from "react";
import { Folder as FolderIcon, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Folder } from "@/lib/api-types";
import { FolderCardMenu } from "./folder-card-menu";

type Props = {
  folder: Folder;
  view: "grid" | "list";
  /** e.g. `/app/projects/:pid/folders` — folder id is appended to build the link. */
  foldersBasePath: string;
  /** Project id for cache-invalidation of the parent listing on rename/delete. */
  projectId: string;
  /** Selection state for master Share (Task 12). Optional — not every caller wires selection. */
  selected?: boolean;
  onToggleSelected?: () => void;
  /** Card body single-click — replaces the entire selection with just this
   *  folder (2026-07-18 spec). Optional so old callers keep compiling. */
  onSelectExclusive?: () => void;
};

/** Same checkbox pattern as VideoCard — stopPropagation so parent Link doesn't navigate. */
function SelectCheckbox({
  checked,
  onToggle,
  className,
}: {
  checked: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <input
      type="checkbox"
      aria-label="Select folder"
      checked={checked}
      onClick={(e) => e.stopPropagation()}
      onChange={onToggle}
      className={className}
    />
  );
}

/**
 * Folder card — clickable when `folder.canEnter`, shows a lock badge and stays
 * inert when a restricted ancestor blocks entry. Item counts come from the
 * backend `itemCount` (direct children only — does NOT recurse) — matches the
 * Frame.io behaviour of "1 file, 2 folders" being a shallow count.
 */
export function FolderCard({
  folder,
  view,
  foldersBasePath,
  projectId,
  selected = false,
  onToggleSelected,
  onSelectExclusive,
}: Props) {
  const navigate = useNavigate();
  const locked = !folder.canEnter;
  const href = `${foldersBasePath}/${folder.id}`;
  const itemLabel =
    folder.itemCount == null
      ? "—"
      : folder.itemCount === 0
        ? "No Items"
        : `${folder.itemCount} ${folder.itemCount === 1 ? "Item" : "Items"}`;
  // Selection is available only when the caller wires it AND the folder is
  // unlocked (locked folders can't be shared — no access).
  const showCheckbox = !!onToggleSelected && !locked;

  // Single-click card body = replace selection (deferred 220ms so a fast
  // second click promotes to double-click / open). Same pattern as VideoCard.
  const clickTimerRef = useRef<number | null>(null);
  const handleClick = (e: React.MouseEvent) => {
    if (locked) return;
    e.stopPropagation();
    if (clickTimerRef.current !== null) return;
    clickTimerRef.current = window.setTimeout(() => {
      clickTimerRef.current = null;
      (onSelectExclusive ?? onToggleSelected)?.();
    }, 220);
  };
  const handleDoubleClick = (e: React.MouseEvent) => {
    if (locked) return;
    e.stopPropagation();
    if (clickTimerRef.current !== null) {
      window.clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    navigate(href);
  };

  // Card visuals are shared between grid + list; the wrapper picks which the
  // outer element is (Link vs. plain div) so a locked folder can't be clicked.
  const commonListInner = (
    <>
      {showCheckbox && (
        <SelectCheckbox checked={selected} onToggle={onToggleSelected!} className="shrink-0" />
      )}
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-secondary text-muted-foreground">
        {locked ? (
          <Lock className="h-4 w-4" strokeWidth={1.75} />
        ) : (
          <FolderIcon className="h-4 w-4" strokeWidth={1.75} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="truncate font-medium text-foreground">{folder.name}</div>
          {folder.restricted && (
            <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Restricted
            </span>
          )}
        </div>
      </div>
      <span className="shrink-0 text-xs text-muted-foreground">{itemLabel}</span>
      <FolderCardMenu folder={folder} projectId={projectId} />
    </>
  );

  const commonGridInner = (
    <>
      <div className="relative grid aspect-video place-items-center rounded-lg bg-secondary/60">
        {showCheckbox && (
          <SelectCheckbox
            checked={selected}
            onToggle={onToggleSelected!}
            className="absolute left-2 top-2 z-10"
          />
        )}
        <FolderIcon
          className={
            locked
              ? "h-8 w-8 text-muted-foreground/50"
              : "h-8 w-8 text-muted-foreground"
          }
          strokeWidth={1.5}
        />
        {/* Restricted-flag badge — top-right of the thumbnail, always visible
            when restricted (matches Frame.io). Locked (no-access) folders
            add the center overlay below on top of this. */}
        {folder.restricted && (
          <div className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full bg-background/85 text-foreground shadow-sm ring-1 ring-border">
            <Lock className="h-3.5 w-3.5" strokeWidth={2} />
          </div>
        )}
        {locked && (
          <div className="absolute inset-0 grid place-items-center rounded-lg bg-background/40 backdrop-blur-[1px]">
            <div className="flex items-center gap-1.5 rounded-full bg-background/80 px-2.5 py-1 text-xs font-medium text-muted-foreground">
              <Lock className="h-3.5 w-3.5" strokeWidth={2} />
              Restricted
            </div>
          </div>
        )}
      </div>
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-foreground">
            {folder.name}
          </div>
          <div className="text-xs text-muted-foreground">{itemLabel}</div>
        </div>
        <FolderCardMenu folder={folder} projectId={projectId} />
      </div>
    </>
  );

  if (view === "list") {
    if (locked) {
      return (
        <div className="flex cursor-not-allowed items-center gap-4 rounded-lg border border-border bg-card px-4 py-3 opacity-70">
          {commonListInner}
        </div>
      );
    }
    return (
      <div
        data-selectable-card
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        className={
          "flex cursor-pointer items-center gap-4 rounded-lg border bg-card px-4 py-3 transition-colors hover:bg-secondary/40 " +
          (selected ? "border-primary/60 ring-1 ring-primary/40" : "border-border")
        }
      >
        {commonListInner}
      </div>
    );
  }

  if (locked) {
    return (
      <div className="flex cursor-not-allowed flex-col gap-2 rounded-xl border border-border bg-card p-3 opacity-70">
        {commonGridInner}
      </div>
    );
  }
  return (
    <div
      data-selectable-card
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      className={
        "flex cursor-pointer flex-col gap-2 rounded-xl border bg-card p-3 transition-colors hover:bg-secondary/30 " +
        (selected ? "border-primary/60 ring-1 ring-primary/40" : "border-border")
      }
    >
      {commonGridInner}
    </div>
  );
}
